import { oc } from '@orpc/contract';
import * as z from 'zod';
import { youTubeVideoIdSchema } from '../domain/youtube.js';
import { childIdSchema, collectionIdSchema, contentItemIdSchema, importJobIdSchema, importSourceIdSchema, } from '../primitives/id.js';
import { languageSchema } from '../primitives/language.js';
import { instantSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
const base = `/${API_VERSION}/imports`;
const sources = `/${API_VERSION}/import-sources`;
/**
 * Bringing a whole source into the library at once.
 *
 * A parent adding one video at a time is fine for a handful and absurd for a
 * channel with two thousand. This turns "here is a channel" into a reviewable
 * list, then into local files the family owns a copy of.
 *
 * **Local files, not embeds.** An imported video becomes an ordinary `LOCAL`
 * content item: no advertising, no route out to YouTube, and it plays with no
 * connection at all. That is a better environment for a small child than the
 * embedded player, and it is the reason this exists — see `SPEC.md` §11.4 for
 * what it costs and when it must not be used.
 *
 * Importing is still not approving. Items arrive in the library and reach a
 * child only when a parent says so, which is the same rule the rest of the
 * catalogue follows (§2.1).
 */
export const importSourceKindSchema = z.enum([
    'YOUTUBE_CHANNEL',
    'YOUTUBE_PLAYLIST',
    'YOUTUBE_VIDEO',
]);
/** What a source turned out to be, before anything is downloaded. */
export const importSourceSchema = z.object({
    kind: importSourceKindSchema,
    title: z.string().min(1).max(200),
    /**
     * How many the source holds in total, where the provider says so.
     *
     * Null rather than a guess: a channel's video tab reports nothing until it
     * has been paged to the end, and inventing a number for a progress bar is
     * how a progress bar starts lying.
     */
    totalCount: z.int().nonnegative().nullable(),
    /**
     * The channel's own mark, if it has one.
     *
     * Handed to the parent and handed straight back on `start`, rather than the
     * backend resolving the channel twice. The image is fetched and kept as a
     * file at that point — a child's tablet never asks a third party for it.
     */
    posterUrl: z.url().max(500).nullable(),
});
/** A copy of a video the family already has, from an earlier import. */
export const importHeldCopySchema = z.object({
    contentItemId: contentItemIdSchema,
    /** What the download reported. Null when the track said nothing. */
    audioLanguage: languageSchema.nullable(),
});
/** One video the parent may choose to bring in. */
export const importCandidateSchema = z.object({
    videoId: youTubeVideoIdSchema,
    title: z.string().min(1).max(200),
    durationSeconds: z.int().nonnegative().nullable(),
    /**
     * The copies already in the library, one per audio language.
     *
     * Coming back to a channel for its new episodes is the ordinary case, and a
     * listing that cannot tell old from new makes the parent download the same
     * thing twice. Per language rather than a flag, because the same episode in
     * Turkish and in English are two things a parent may want.
     */
    inLibrary: z.array(importHeldCopySchema),
    /** Waiting or downloading in an import that is still running. */
    isQueued: z.boolean(),
});
/** One chosen video, with the title the parent saw it under. */
export const importChoiceSchema = importCandidateSchema.pick({ videoId: true, title: true });
export const importItemStatusSchema = z.enum(['PENDING', 'DOWNLOADING', 'ADDED', 'FAILED']);
export const importJobStatusSchema = z.enum(['RUNNING', 'COMPLETED', 'CANCELLED']);
export const importJobItemSchema = z.object({
    videoId: youTubeVideoIdSchema,
    title: z.string().min(1).max(200),
    status: importItemStatusSchema,
    /** Set once the download has landed and the library row exists. */
    contentItemId: contentItemIdSchema.nullable(),
    /**
     * A machine code, never prose. A video can be private, age-gated, removed, or
     * simply refuse to download today; the parent interface says which.
     */
    failureCode: z.string().max(60).nullable(),
});
export const importJobSchema = z.object({
    id: importJobIdSchema,
    status: importJobStatusSchema,
    sourceTitle: z.string().min(1).max(200),
    /** The audio the parent asked for. Null means each video's own original. */
    audioLanguage: languageSchema.nullable(),
    total: z.int().nonnegative(),
    completed: z.int().nonnegative(),
    failed: z.int().nonnegative(),
    createdAt: instantSchema,
});
/**
 * A source the parent has listed before, to list again without pasting it.
 *
 * `url` is the canonical address `resolveImportSource` produced, which `inspect`
 * accepts as it is.
 */
export const recentImportSourceSchema = z.object({
    id: importSourceIdSchema,
    url: z.url().max(500),
    kind: importSourceKindSchema,
    title: z.string().min(1).max(200),
    posterUrl: z.url().max(500).nullable(),
    lastInspectedAt: instantSchema,
});
/** How many recent sources are offered. A short list is a shortcut; a long one is a search. */
export const RECENT_IMPORT_SOURCES_LIMIT = 8;
/** The ceiling on one listing. Paging further is a second request. */
export const IMPORT_INSPECT_LIMIT = 200;
export const importsContract = {
    /**
     * Reads a source without downloading anything.
     *
     * Deliberately separate from starting an import: a parent should see what
     * they are about to bring in — how many, how long each one is — before a
     * single byte moves. Nothing here writes to the library.
     */
    inspect: oc
        .route({ method: 'POST', path: `${base}/inspect` })
        .input(z.object({
        url: z.string().min(1).max(500),
        limit: z.int().positive().max(IMPORT_INSPECT_LIMIT).optional(),
        /**
         * The language to read titles in — the parent's own.
         *
         * Channels for small children often publish in one language and
         * translate their titles into others; without this, the provider hands
         * back whichever it considers default, which for many Turkish channels
         * is English. A title with no translation arrives as written.
         */
        titleLanguage: languageSchema.optional(),
    }))
        .output(z.object({ source: importSourceSchema, candidates: z.array(importCandidateSchema) }))
        .errors({
        INVALID_SOURCE_URL: { status: 400, message: 'That does not look like a YouTube source' },
        SOURCE_NOT_FOUND: { status: 404, message: 'That source is unavailable' },
        IMPORT_UNAVAILABLE: {
            status: 503,
            message: 'Importing is not configured on this server',
        },
    }),
    /**
     * Starts downloading the chosen videos.
     *
     * Returns as soon as the job exists rather than when it finishes: a hundred
     * videos take hours, and a request that waits for them is a request that
     * times out. Progress is polled.
     */
    start: oc
        .route({ method: 'POST', path: base })
        .input(z.object({
        sourceTitle: z.string().min(1).max(200),
        /**
         * `inspect`'s candidates, as chosen.
         *
         * The title travels with the id because the listing is where it was
         * read in the parent's language. The download cannot ask for it the
         * same way: the provider then translates its error messages too, and a
         * refusal nobody can classify is a refusal nobody retries.
         */
        videos: z.array(importChoiceSchema).min(1).max(IMPORT_INSPECT_LIMIT),
        /** Filed here as they arrive, so a big import is not loose in the library. */
        collectionId: collectionIdSchema.optional(),
        /**
         * `inspect`'s `source.posterUrl`, passed back unchanged.
         *
         * Becomes the collection's picture, once, if it does not have one. The
         * backend refuses any host but the provider's own image servers: this
         * is a URL that arrived in a request, and fetching an arbitrary one on
         * a caller's say-so is how a backend becomes somebody's proxy.
         */
        sourcePosterUrl: z.url().max(500).nullable().optional(),
        /**
         * Opened to these children as each one lands.
         *
         * Empty is allowed and is the safe default: the videos sit in the
         * library until a parent gives them to somebody by name.
         */
        childIds: z.array(childIdSchema),
        /**
         * Which audio track to download. A video without one in this language
         * fails with `AUDIO_LANGUAGE_UNAVAILABLE` rather than arriving in
         * another: a parent who asked for English practice and got Turkish has
         * been told something untrue. Omitted, each video's original is kept.
         */
        audioLanguage: languageSchema.optional(),
    }))
        .output(importJobSchema)
        .errors({
        IMPORT_UNAVAILABLE: {
            status: 503,
            message: 'Importing is not configured on this server',
        },
        CHILD_NOT_FOUND: { status: 404, message: 'Child not found' },
        COLLECTION_NOT_FOUND: { status: 404, message: 'Collection not found' },
    }),
    get: oc
        .route({ method: 'GET', path: `${base}/{importJobId}` })
        .input(z.object({ importJobId: importJobIdSchema }))
        .output(z.object({ job: importJobSchema, items: z.array(importJobItemSchema) }))
        .errors({ IMPORT_JOB_NOT_FOUND: { status: 404, message: 'Import job not found' } }),
    list: oc
        .route({ method: 'GET', path: base })
        .output(z.object({ jobs: z.array(importJobSchema) })),
    /**
     * The sources this family listed most recently, newest first.
     *
     * Recorded by `inspect` when a listing succeeds, so a channel checked and
     * found to hold nothing new is remembered as well as one imported from.
     */
    recentSources: oc
        .route({ method: 'GET', path: sources })
        .output(z.object({ sources: z.array(recentImportSourceSchema) })),
    /** Takes a source off the recent list. Nothing in the library changes. */
    forgetSource: oc
        .route({ method: 'DELETE', path: `${sources}/{importSourceId}` })
        .input(z.object({ importSourceId: importSourceIdSchema }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ IMPORT_SOURCE_NOT_FOUND: { status: 404, message: 'Import source not found' } }),
    /** Stops a running job. What has already landed stays in the library. */
    cancel: oc
        .route({ method: 'POST', path: `${base}/{importJobId}/cancel` })
        .input(z.object({ importJobId: importJobIdSchema }))
        .output(importJobSchema)
        .errors({ IMPORT_JOB_NOT_FOUND: { status: 404, message: 'Import job not found' } }),
};
//# sourceMappingURL=imports.js.map